import { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { LineChart, ChevronLeft, ChevronRight, Calendar as CalendarIcon, X, Filter, Lock } from 'lucide-react';
import { useDrop } from 'react-dnd';
import { motion, AnimatePresence } from 'framer-motion';
import type { Post, CommunicationChannel } from '../../types';
import { defaultChannels } from '../../config/channels';
import { CalendarDay } from '../features/calendar/CalendarDay';
import { ChannelSvgIcon } from '../../utils/Constants';
import { useLanguage } from '../../contexts/LanguageContext';
import { getLocaleFromLanguage, capitalizeFirstLetter } from '../../i18n';
import { AnimatedIconButton } from '../common/AnimatedIconButton';
import { HistoryTab } from '../features/tabs/HistoryTab';
// V4-CODESPLIT-DASHBOARD 2026-07-29 — tous les importeurs doivent être lazy,
// sinon Rollup garde le module dans le chunk d'entrée et les `lazy()` des autres
// sites sont inertes (constaté : 0 chunk émis tant que ces 2 imports restaient
// statiques).
const ExpandedPostModal = lazy(() =>
  import('../features/posts/ExpandedPostModal').then(m => ({ default: m.ExpandedPostModal }))
);
import { getMonthDays } from '../../utils/dateUtils';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { Logger } from '../../utils/logger';

interface QueueSidebarProps {
  expanded: boolean;
  onToggle: () => void;
  queue: Post[];
  allDatedPosts?: Post[]; // All posts with a scheduledDate (past + present + future) for calendar display
  newsletterPosts: Post[];
  onAddToQueue: (post: Post, scheduledDate?: Date) => void;
  onAddToNewsletter: (post: Post) => void;
  onUpdatePost: (post: Post, scheduledDate?: Date) => void;
  onUnschedule?: (postId: string) => void;
  onRemoveFromNewsletter: (post: Post) => void;
  onDeletePost?: (postId: string) => void;
  communicationChannels: CommunicationChannel[];
  onDroppedPostForScheduling: (post: Post) => void;
  onScheduleNewsletter?: (scheduledDate: Date, introduction: string, posts: Post[]) => void;
  allPosts?: Post[]; // All posts from gallery to get full post data for newsletters
  onPublishNow?: (postId: string) => Promise<Post | null>;
  onOpen?: () => void;   // called when sidebar transitions from closed → open
  closeKey?: number;     // increment to force sidebar closed
  // V4-SIDEBAR-LOCK-NO-POST 2026-05-28 — désactive l'accès à la file/agenda tant
  // qu'aucun post n'existe (UX cohérente avec Analytics / Engagement gating).
  locked?: boolean;
  lockedTooltip?: string;
}

// Helper to check if two dates are the same day
const isSameDay = (date1: Date, date2: Date) => {
  return date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate();
};

// Date à laquelle un post est placé dans le calendrier Publications :
// sa date de planification si planifié, sinon sa date de publication (publish-now).
// 2026-06-10 — sans le fallback publishedDate, les posts publiés immédiatement
// (sans scheduledDate) n'apparaissaient jamais dans le calendrier.
const getPostCalendarDate = (post: Post): Date | undefined =>
  post.scheduledDate ?? post.publishedDate;

type SidebarMode = 'closed' | 'agenda' | 'history';

export function QueueSidebar({
  queue,
  allDatedPosts,
  newsletterPosts,
  onAddToQueue,
  onAddToNewsletter,
  onUpdatePost,
  onUnschedule,
  onRemoveFromNewsletter,
  onDeletePost,
  communicationChannels,
  onDroppedPostForScheduling,
  onScheduleNewsletter,
  allPosts,
  onPublishNow,
  onOpen,
  closeKey,
  locked = false,
  lockedTooltip,
}: QueueSidebarProps) {
  const [mode, setMode] = useState<SidebarMode>('closed');
  // Dernier mode ouvert, conservé pendant l'animation de fermeture (exit) pour éviter que
  // le contenu (agenda→history) ne flippe pendant les 300ms d'exit quand mode repasse 'closed'.
  const lastOpenModeRef = useRef<Exclude<SidebarMode, 'closed'>>('agenda');
  const [selectedPost, setSelectedPost] = useState<Post | null>(null);
  const [isDroppedPost, setIsDroppedPost] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedChannelIds, setSelectedChannelIds] = useState<string[]>([]);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const { t, language } = useLanguage();
  const locale = getLocaleFromLanguage(language);

  const monthDays = getMonthDays(currentDate.getFullYear(), currentDate.getMonth());

  // Force close when parent signals mutual exclusion
  useEffect(() => {
    if (closeKey) setMode('closed');
  }, [closeKey]);

  // Initialize selected channels with all enabled channels
  useEffect(() => {
    const enabledChannelIds = communicationChannels
      .filter(c => c.enabled !== false)
      .map(c => c.id);
    setSelectedChannelIds(enabledChannelIds);
  }, [communicationChannels]);

  // Filter queue based on selected channels, but always include newsletters
  const filteredQueue = queue.filter(post => {
    // Always include newsletter posts
    if (post.isNewsletter) return true;
    // Filter regular posts by channel
    return post.channelId && selectedChannelIds.includes(post.channelId);
  });

  // All dated posts filtered by channel for calendar display (past + present + future)
  const calendarPosts = (allDatedPosts || queue).filter(post => {
    if (post.isNewsletter) return true;
    return post.channelId && selectedChannelIds.includes(post.channelId);
  });

  const handleNextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
    setSelectedDay(null);
  };

  const handlePrevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
    setSelectedDay(null);
  };

  const handleAgendaClick = () => {
    // V4-SIDEBAR-LOCK-NO-POST 2026-05-28 — silently bail si locked (user
    // n'a pas encore généré de post). Le tooltip explique la raison.
    if (locked) return;
    if (mode === 'agenda') {
      setMode('closed');
    } else {
      setMode('agenda');
      onOpen?.();
    }
  };

  const handleHistoryClick = () => {
    if (mode === 'history') {
      setMode('closed');
    } else {
      setMode('history');
      onOpen?.();
    }
  };

  const toggleChannelFilter = (channelId: string) => {
    setSelectedChannelIds(prev =>
      prev.includes(channelId)
        ? prev.filter(id => id !== channelId)
        : [...prev, channelId]
    );
  };

  const clearFilters = () => {
    const enabledChannelIds = communicationChannels
      .filter(c => c.enabled !== false)
      .map(c => c.id);
    setSelectedChannelIds(enabledChannelIds);
  };

  // Drop zone for agenda
  const [{ isOverAgenda }, dropAgenda] = useDrop(() => ({
    accept: 'post',
    drop: (item: Post) => {
      if (!item.scheduledDate) {
        // Trigger global modal for scheduling
        onDroppedPostForScheduling(item);
      }
    },
    collect: (monitor) => ({
      isOverAgenda: monitor.isOver(),
    }),
  }));

  // Drop zone for newsletter
  const [{ isOverNewsletter }, dropNewsletter] = useDrop(() => ({
    accept: 'post',
    drop: (item: Post) => {
      onAddToNewsletter(item);
    },
    collect: (monitor) => ({
      isOverNewsletter: monitor.isOver(),
    }),
  }));

  // Calculer le nombre de posts en attente (scheduledDate > maintenant)
  const pendingPostsCount = queue.filter(post => {
    if (!post.scheduledDate) return false;
    return new Date(post.scheduledDate) > new Date();
  }).length;

  // Compter les posts dans la prochaine newsletter non publiée
  const upcomingNewsletterPostsCount = newsletterPosts.filter(post => {
    if (!post.scheduledDate) return true; // Non programmée = en attente
    return new Date(post.scheduledDate) > new Date();
  }).length;

  // Posts for the selected day
  const selectedDayPosts = selectedDay
    ? calendarPosts.filter((post) => {
        const d = getPostCalendarDate(post);
        return d && isSameDay(new Date(d), selectedDay);
      })
    : [];

  useEffect(() => {
    Logger.log('QueueSidebar mounted, mode:', mode);
  }, [mode]);

  if (mode !== 'closed') lastOpenModeRef.current = mode;
  const displayMode = mode === 'closed' ? lastOpenModeRef.current : mode;

  return (
    <>
      {/* Collapsed bar — toujours montée ; le drawer s'anime par-dessus via AnimatePresence. */}
        <div
          className="fixed right-0 top-0 h-screen w-16 flex flex-col z-50"
          style={{
            backgroundColor: 'var(--color-background)',
            borderLeft: `1px solid var(--color-border)`
          }}
        >
          {/*
            Agenda/Publications scheduling feature — ENABLED.
            Posts can be dragged here to schedule them via the calendar.
            The backend publication scheduler auto-publishes posts when scheduled_date is reached.
          */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  className={`flex-1 flex items-center justify-center transition-colors ${locked ? 'cursor-not-allowed opacity-40' : 'cursor-pointer hover:opacity-80'}`}
                  style={{ backgroundColor: 'transparent' }}
                  onClick={handleAgendaClick}
                  aria-disabled={locked}
                  data-testid="queue-sidebar-agenda-trigger"
                >
                  <div
                    className="flex items-center justify-center w-10 h-10 rounded-full relative"
                    style={{ backgroundColor: 'transparent' }}
                  >
                    <CalendarIcon
                      className="w-5 h-5 flex-shrink-0"
                      style={{ color: 'var(--color-text-primary)' }}
                    />
                    {/* V4-SIDEBAR-LOCK-NO-POST 2026-05-28 — pastille cadenas */}
                    {locked && (
                      <span
                        className="absolute -bottom-0.5 -right-0.5 flex items-center justify-center rounded-full"
                        style={{
                          width: 16,
                          height: 16,
                          backgroundColor: 'var(--color-warning-light, rgba(245, 158, 11, 0.15))',
                          border: '1px solid var(--color-warning)',
                        }}
                        aria-hidden="true"
                      >
                        <Lock style={{ width: 9, height: 9, color: 'var(--color-warning)' }} />
                      </span>
                    )}
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent side="left">
                <p className="text-sm">{locked ? (lockedTooltip ?? t.sidebar?.publicationsComingSoon ?? 'Publications') : (t.sidebar?.publicationsComingSoon || 'Publications')}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>

          {/*
            V4-ANALYTICS-HISTORY 2026-06-04 — "History" panel (post-publication analytics).
            Replaces the disabled Newsletter zone (Newsletter deferred to a future release,
            cf. backlog V4-NEWSLETTER-RELEASE).
          */}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div
                  className="flex-1 flex items-center justify-center transition-colors cursor-pointer hover:opacity-80"
                  style={{ backgroundColor: 'transparent' }}
                  onClick={handleHistoryClick}
                  data-testid="queue-sidebar-history-trigger"
                >
                  <div
                    className="flex items-center justify-center w-10 h-10 rounded-full relative"
                    style={{ backgroundColor: 'transparent' }}
                  >
                    <LineChart
                      className="w-5 h-5 flex-shrink-0"
                      style={{ color: 'var(--color-text-primary)' }}
                    />
                  </div>
                </div>
              </TooltipTrigger>
              <TooltipContent side="left">
                <p className="text-sm">{t.sidebar?.historyTooltip || t.menu.history || 'Historique'}</p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

      {/* Overlay + drawer avec animation enter/exit (mirror du drawer gauche Sidebar.tsx).
          AnimatePresence garde l'élément monté le temps de l'exit → animation de fermeture. */}
      <AnimatePresence>
        {mode !== 'closed' && (
          <motion.div
            key="qs-overlay"
            className="fixed inset-0 z-40 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            style={{ backgroundColor: 'rgba(var(--color-background-rgb), 0.8)' }}
            onClick={() => setMode('closed')}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {mode !== 'closed' && (
      <motion.div
        key="qs-drawer"
        initial={{ width: 64, opacity: 0 }}
        animate={{ width: '50%', opacity: 1 }}
        exit={{ width: 64, opacity: 0 }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        className="fixed right-0 top-0 h-screen flex flex-col z-50 shadow-[0_10px_15px_-3px_rgba(0,0,0,0.1),0_4px_6px_-4px_rgba(0,0,0,0.1)]"
        data-testid={`queue-sidebar-${displayMode}`}
        style={{
          backgroundColor: 'var(--color-background)',
          borderLeft: `1px solid var(--color-border)`
        }}
      >
        {displayMode === 'agenda' ? (
          <>
            {/* Agenda Header */}
            <div 
              className="p-6 flex-shrink-0"
              style={{
                borderBottom: `1px solid var(--color-border)`,
                backgroundColor: 'var(--color-background)'
              }}
            >
              <div className="flex items-center justify-between">
                <h2 style={{ color: 'var(--color-text-primary)' }}>{t.menu.posts}</h2>
                <button
                  onClick={() => setMode('closed')}
                  data-testid="queue-sidebar-close"
                  className="p-1 rounded-full transition-colors hover:opacity-80"
                  style={{
                    backgroundColor: 'var(--color-surface)'
                  }}
                >
                  <X className="w-5 h-5" style={{ color: 'var(--color-text-secondary)' }} />
                </button>
              </div>
            </div>

            {/* Calendar Navigation Sub-header */}
            <div 
              className="p-4 flex items-center justify-between flex-shrink-0"
              style={{
                borderBottom: `1px solid var(--color-border)`,
                backgroundColor: 'var(--color-background)'
              }}
            >
              {/* Left: Calendar navigation */}
              <div className="flex items-center gap-1">
                <button
                  onClick={handlePrevMonth}
                  className="p-1 rounded-full transition-colors"
                  style={{
                    backgroundColor: 'var(--color-surface)'
                  }}
                >
                  <ChevronLeft className="w-4 h-4" style={{ color: 'var(--color-text-primary)' }} />
                </button>
                <h3 
                  className="text-sm min-w-[140px] text-center"
                  style={{ color: 'var(--color-text-primary)' }}
                >
                  {capitalizeFirstLetter(currentDate.toLocaleDateString(locale, { month: 'long', year: 'numeric' }))}
                </h3>
                <button
                  onClick={handleNextMonth}
                  className="p-1 rounded-full transition-colors"
                  style={{
                    backgroundColor: 'var(--color-surface)'
                  }}
                >
                  <ChevronRight className="w-4 h-4" style={{ color: 'var(--color-text-primary)' }} />
                </button>
              </div>

              {/* Right: Filter button */}
              <AnimatedIconButton
                icon={Filter}
                label={t.posts.filters}
                onClick={() => setShowFilterDropdown(!showFilterDropdown)}
                variant="secondary"
                testId="queue-sidebar-filter-trigger"
              />

              {/* Filter Dropdown */}
              {showFilterDropdown && (
                <div 
                  className="absolute right-6 top-[120px] w-64 rounded-lg shadow-lg py-2 z-50"
                  style={{
                    backgroundColor: 'var(--color-background)',
                    border: `1px solid var(--color-border)`
                  }}
                >
                  <div 
                    className="px-4 py-2 flex items-center justify-between"
                    style={{
                      borderBottom: `1px solid var(--color-border)`
                    }}
                  >
                    <span className="text-sm" style={{ color: 'var(--color-text-primary)' }}>{t.posts.filterByChannel}</span>
                    {selectedChannelIds.length < communicationChannels.filter(c => c.enabled !== false).length && (
                      <button
                        onClick={clearFilters}
                        className="text-xs hover:underline"
                        style={{ color: 'var(--color-primary)' }}
                      >
                        {t.posts.clear}
                      </button>
                    )}
                  </div>
                  <div className="max-h-64 overflow-y-auto py-2">
                    {communicationChannels
                      .filter(c => c.enabled !== false)
                      .map((channel) => {
                        const channelConfig = defaultChannels.find(c => c.name === channel.network);
                        const isSelected = selectedChannelIds.includes(channel.id);
                        
                        return (
                          <button
                            key={channel.id}
                            onClick={() => toggleChannelFilter(channel.id)}
                            className="w-full px-4 py-2 flex items-center gap-3 transition-colors group"
                            style={{
                              backgroundColor: isSelected ? 'transparent' : 'transparent'
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.backgroundColor = 'var(--color-surface)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }}
                          >
                            <div
                              className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                                isSelected ? '' : 'opacity-50'
                              }`}
                            >
                              <ChannelSvgIcon network={channel.network} size={20} />
                            </div>
                            <span 
                              className={`text-sm ${isSelected ? '' : 'opacity-70'}`}
                              style={{ color: 'var(--color-text-primary)' }}
                            >
                              {channel.customName || (channelConfig?.translationKey && t.networks ? t.networks[channelConfig.translationKey as keyof typeof t.networks] : channelConfig?.displayName) || channel.network}
                            </span>
                          </button>
                        );
                      })}
                  </div>
                </div>
              )}
            </div>

            {/* Scrollable zone — calendrier + liste du jour */}
            <div className="flex-1 min-h-0 overflow-y-auto">
              {/* Calendar Grid with Posts */}
              <div className="p-3">
                <div className="grid grid-cols-7 gap-2">
                  {/* Day headers */}
                  {t.common.weekDays.map((day, index) => (
                    <div
                      key={`day-header-${index}`}
                      className="text-center text-xs pb-1"
                      style={{ color: 'var(--color-text-secondary)' }}
                    >
                      {day}
                    </div>
                  ))}

                  {/* Calendar days */}
                  {monthDays.map((day) => {
                    const isCurrentMonth = day.getMonth() === currentDate.getMonth();
                    const isToday = isSameDay(day, new Date());
                    const dayPosts = calendarPosts.filter((post) => {
                      const d = getPostCalendarDate(post);
                      return d && isSameDay(new Date(d), day);
                    });

                    return (
                      <CalendarDay
                        key={`${day.getFullYear()}-${day.getMonth()}-${day.getDate()}`}
                        day={day}
                        isCurrentMonth={isCurrentMonth}
                        isToday={isToday}
                        isSelected={selectedDay !== null && isSameDay(day, selectedDay)}
                        posts={dayPosts}
                        communicationChannels={communicationChannels}
                        onPostClick={(post) => { setSelectedPost(post); setMode('closed'); }}
                        onUpdatePost={onUpdatePost}
                        onDayClick={(d) => setSelectedDay(prev => prev && isSameDay(prev, d) ? null : d)}
                      />
                    );
                  })}
                </div>
              </div>

              {/* Selected Day Posts List */}
              {selectedDay && (
                <div
                  className="border-t"
                  style={{ borderColor: 'var(--color-border)' }}
                >
                  <div className="px-4 py-3 flex items-center justify-between sticky top-0 z-10" style={{ backgroundColor: 'var(--color-background)', borderBottom: '1px solid var(--color-border)' }}>
                    <span className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                      {capitalizeFirstLetter(selectedDay.toLocaleDateString(locale, { weekday: 'long', day: 'numeric', month: 'long' }))}
                    </span>
                    <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                      {selectedDayPosts.length} {selectedDayPosts.length > 1 ? 'posts' : 'post'}
                    </span>
                  </div>
                  {selectedDayPosts.length === 0 ? (
                    <div className="p-4 text-center text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                      {t.posts.noPostsForDay || 'Aucun post pour ce jour'}
                    </div>
                  ) : (
                    <div className="divide-y" style={{ borderColor: 'var(--color-border)' }}>
                      {selectedDayPosts
                        .sort((a, b) => {
                          const da = getPostCalendarDate(a);
                          const db = getPostCalendarDate(b);
                          const dateA = da ? new Date(da).getTime() : 0;
                          const dateB = db ? new Date(db).getTime() : 0;
                          return dateA - dateB;
                        })
                        .map((post) => {
                          const channel = communicationChannels.find(c => c.id === post.channelId);
                          const postDate = getPostCalendarDate(post);
                          const time = postDate
                            ? new Date(postDate).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })
                            : '--:--';
                          return (
                            <button
                              key={post.id}
                              onClick={() => { setSelectedPost(post); setMode('closed'); }}
                              className="w-full px-4 py-3 flex items-center gap-3 text-left transition-colors hover:opacity-80"
                              style={{ backgroundColor: 'transparent' }}
                              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--color-surface)'; }}
                              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                            >
                              <ChannelSvgIcon network={channel?.network} size={24} />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm truncate" style={{ color: 'var(--color-text-primary)' }}>
                                  {post.content.slice(0, 80)}{post.content.length > 80 ? '…' : ''}
                                </p>
                                <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-secondary)' }}>
                                  {channel?.customName || channel?.network || '—'}
                                </p>
                              </div>
                              <span className="text-xs font-mono flex-shrink-0" style={{ color: 'var(--color-text-secondary)' }}>
                                {time}
                              </span>
                            </button>
                          );
                        })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        ) : (
          <>
            {/* History Header */}
            <div
              className="p-6 flex items-center justify-between flex-shrink-0"
              style={{
                borderBottom: `1px solid var(--color-border)`,
                backgroundColor: 'var(--color-background)'
              }}
            >
              <h2 style={{ color: 'var(--color-text-primary)' }}>{t.history?.title ?? t.menu.history}</h2>
              <button
                onClick={() => setMode('closed')}
                className="p-1 rounded-full transition-colors hover:opacity-80"
                style={{
                  backgroundColor: 'var(--color-surface)'
                }}
              >
                <X className="w-5 h-5" style={{ color: 'var(--color-text-secondary)' }} />
              </button>
            </div>

            {/* History Content — Figma-faithful timeline (1 point = 1 published post).
                height/minHeight/overflow inline : min-h-0 absent du CSS Tailwind figé. */}
            <div className="flex-1" style={{ height: '100%', minHeight: 0, overflow: 'hidden' }}>
              <HistoryTab
                onPostClick={(postId) => {
                  const found =
                    (allPosts || []).find((p) => p.id === postId) ||
                    (allDatedPosts || []).find((p) => p.id === postId) ||
                    queue.find((p) => p.id === postId);
                  if (found) {
                    setSelectedPost(found);
                    setMode('closed');
                  }
                }}
              />
            </div>
          </>
        )}

      </motion.div>
        )}
      </AnimatePresence>

      {/* ExpandedPostModal — always at root level, independent of sidebar mode */}
      {selectedPost && (
        <Suspense fallback={null}>
          <ExpandedPostModal
          post={selectedPost}
          onClose={() => {
            setSelectedPost(null);
            setIsDroppedPost(false);
          }}
          onAddToQueue={onAddToQueue}
          onUpdatePost={onUpdatePost}
          onUnschedule={onUnschedule}
          onDelete={onDeletePost}
          communicationChannels={communicationChannels}
          isInQueue={!isDroppedPost}
          isInNewsletter={newsletterPosts.some(p => p.id === selectedPost.id)}
          openDatePickerOnMount={isDroppedPost}
          queuePosts={queue}
          allPosts={allPosts}
          onPublishNow={onPublishNow}
        />
        </Suspense>
      )}
    </>
  );
}
